import React, { useEffect, useRef, useState } from 'react';
import {
  X, Copy, RefreshCw, AlertTriangle, CheckCircle, Pill, FlaskConical, Calendar, Stethoscope, User,
} from 'lucide-react';
import useDialog from '../../hooks/useDialog';
import { formatClinicDate } from '../../utils/clinicTime';
import { onActivateKey, ROW_FOCUS_RING } from '../../utils/keyboard';
import TablePager, { usePagedRows } from '../TablePager';

/**
 * Filling a new prescription from one of the patient's earlier ones.
 *
 * The copy itself runs on the server in one transaction (POST /prescriptions/{id}/copy-from/{sourceId}),
 * which re-checks every medicine against today's stock and reports what it left out. This module
 * holds the picker the doctor chooses from, the rule for which earlier prescriptions may be offered,
 * and the strip on the Diagnosis step that starts a copy and reports on the last one.
 */

// Only a prescription the doctor actually finished is a sensible starting point. INITIATED and DRAFT
// are someone's unfinished work, and COMPLETED is written only by a referral, as a hand-over note
// that never passed the completion checks. The server refuses the same statuses.
const COPYABLE_STATUSES = new Set(['PRESCRIBED', 'INVOICED', 'ISSUED']);

// Both dates come from the server as ISO-8601 text (a plain date, and a date-time with no zone),
// which sorts chronologically as text. Parsing them is not needed, and new Date() on the date-only
// one would land on the previous day for any browser west of UTC.
const newestFirst = (x, y) => {
  const a = String(x || '');
  const b = String(y || '');
  if (a === b) return 0;
  return a < b ? 1 : -1;
};

/**
 * The earlier prescriptions this one may be filled from: the patient's finished prescriptions
 * other than this one, newest first. Two written on the same day fall back to when each was
 * started, since the summaries endpoint orders by date alone and leaves same-day rows in any order.
 */
export const copySourcesFrom = (summaries, currentId) =>
  (Array.isArray(summaries) ? summaries : [])
    .filter((s) => s && s.prescriptionId !== currentId && COPYABLE_STATUSES.has(s.status))
    .sort((a, b) => newestFirst(a.prescriptionDate, b.prescriptionDate) || newestFirst(a.createdAt, b.createdAt));

/**
 * What the writer's "unchanged" baseline becomes after a copy, before it is fingerprinted.
 *
 * Only the fields the copy wrote are now saved on the server. Every other field keeps the value it
 * was last saved with, so an edit made before the copy and not yet saved (vitals re-taken, past lab
 * reports typed, the appointment ticked) still counts as unsaved, and the recovery draft keeps being
 * written for it. Taking the whole form as the new baseline would quietly drop those edits on a
 * refresh.
 *
 * `baseline` is the old baseline as JSON text, `next` the form after the copy, `copied` the fields
 * the copy set. The result follows `next`'s key order, because baselines are compared as JSON text
 * and the same values in another order would read as a change. Null when the old baseline cannot
 * be read; the caller then falls back to the whole form.
 */
export const baselineAfterCopy = (baseline, next, copied) => {
  let base;
  try {
    base = JSON.parse(baseline);
  } catch (e) {
    return null;
  }
  if (!base || typeof base !== 'object' || Array.isArray(base)) return null;
  const fromCopy = copied || {};
  const merged = {};
  Object.keys(next || {}).forEach((key) => {
    merged[key] = Object.prototype.hasOwnProperty.call(fromCopy, key) ? fromCopy[key] : base[key];
  });
  return merged;
};

const SHORT_DATE = { day: 'numeric', month: 'short', year: 'numeric' };

/** "20 Sept 2026", from the prescription's date or, failing that, when it was started. */
export const copySourceDate = (value) => formatClinicDate(value, SHORT_DATE, '');

/**
 * Doctor names arrive with or without their title. Add "Dr" only when the name carries no title at
 * all: a stored "Dr." or "Prof." stays as it is, and so does "Mr." or "Ms.", or it would read "Dr Mr.".
 */
export const doctorWithTitle = (name) => {
  const n = String(name || '').trim();
  if (!n) return '';
  return /^(dr|prof|mr|mrs|ms|miss)\b\.?/i.test(n) ? n : `Dr ${n}`;
};

// The server's reasons for leaving a line out, in the words the doctor reads on the Diagnosis step.
const SKIP_REASON_LABEL = {
  OUT_OF_STOCK: 'Out of stock',
  EXPIRED: 'Expired',
  NOT_CARRIED: 'No longer carried',
  FREQUENCY_RETIRED: 'Frequency needs choosing again',
  INVALID_DOSING: 'Dosing needs checking',
  NOT_OFFERED: 'Lab test no longer offered',
  NOT_IN_LIST: 'Instruction not in your list',
};

export const copySkipReason = (item) =>
  SKIP_REASON_LABEL[item?.reason] || item?.message || 'Not copied';

const WARNING_LABEL = {
  LOW_STOCK: 'Low stock',
};

const warningText = (w) =>
  WARNING_LABEL[w?.reason] || WARNING_LABEL[w?.kind] || w?.message || 'Check before completing';

/**
 * The strip at the top of the Diagnosis step. Before a copy it offers one; after a copy it names the
 * source, asks for a review, and lists what the server left out or wants checked.
 *
 * `copiedFrom` is { number, date, doctorName } (from the copy response, or rebuilt after a reload
 * from the prescription's copiedFromNumber). `result` is the last copy response in this session,
 * the only place the skipped lines and warnings are known; after a reload it is null and only the
 * banner shows.
 *
 * Both states share one layout, with the action button in the same place, so React keeps that
 * button when the strip turns from the offer into the banner. That happens in the same render that
 * closes the picker, and the picker hands focus back to the button that opened it: a new button in
 * its place would leave focus nowhere, and the next Tab would start from the top of the page.
 */
export const CopyPreviousStrip = ({ copiedFrom, result, onOpen, disabled = false }) => {
  const skipped = copiedFrom && Array.isArray(result?.skipped) ? result.skipped : [];
  const warnings = copiedFrom && Array.isArray(result?.warnings) ? result.warnings : [];
  const when = copiedFrom ? copySourceDate(copiedFrom.date) : '';
  const who = copiedFrom ? doctorWithTitle(copiedFrom.doctorName) : '';
  const detail = [when, who].filter(Boolean).join(', ');

  return (
    <div
      className="rounded-xl border border-emerald-200 bg-emerald-50 px-4 py-3 space-y-2.5"
      role={copiedFrom ? 'status' : undefined}
    >
      <div className={`flex ${copiedFrom ? 'items-start' : 'items-center'} justify-between gap-3 flex-wrap`}>
        {copiedFrom ? (
          <p className="flex items-start gap-2 text-sm text-emerald-900 min-w-0">
            <CheckCircle className="w-4 h-4 mt-0.5 flex-shrink-0 text-emerald-600" aria-hidden="true" />
            <span>
              Copied from <span className="font-semibold">{copiedFrom.number || 'a previous prescription'}</span>
              {detail ? ` (${detail})` : ''}. Review every step before completing.
            </span>
          </p>
        ) : (
          <div className="flex items-center gap-2 text-sm text-emerald-800 min-w-0">
            <Copy className="w-4 h-4 flex-shrink-0" aria-hidden="true" />
            <span className="font-medium">Start from a previous prescription</span>
          </div>
        )}
        <button
          type="button"
          onClick={onOpen}
          disabled={disabled}
          className={copiedFrom
            ? 'text-sm font-medium text-emerald-700 hover:text-emerald-800 hover:underline disabled:opacity-50 flex-shrink-0'
            : 'inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-semibold text-white bg-emerald-600 hover:bg-emerald-700 shadow-sm disabled:opacity-50'}
        >
          {copiedFrom ? 'Copy a different one' : (
            <><Copy className="w-4 h-4" aria-hidden="true" />Copy previous prescription</>
          )}
        </button>
      </div>

      {skipped.length > 0 && (
        <div className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-900">
          <div className="font-semibold mb-1">Not copied</div>
          <ul className="space-y-0.5">
            {skipped.map((s, i) => (
              <li key={`${s.kind}-${s.name}-${i}`}>
                <span className="font-medium">{s.name || 'Unnamed item'}</span>: {copySkipReason(s)}
              </li>
            ))}
          </ul>
        </div>
      )}

      {warnings.length > 0 && (
        <div className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-900">
          <div className="font-semibold mb-1">Copied, but check</div>
          <ul className="space-y-0.5">
            {warnings.map((w, i) => (
              <li key={`${w.kind}-${w.name}-${i}`}>
                <span className="font-medium">{w.name || 'Unnamed item'}</span>: {warningText(w)}
              </li>
            ))}
          </ul>
        </div>
      )}

      {copiedFrom && result?.complaintDurationsCleared && (
        <p className="text-sm text-amber-800">Complaint durations were not copied; add today's.</p>
      )}
    </div>
  );
};

/**
 * The picker: the patient's earlier finished prescriptions as a table, newest first, with a preview
 * of the chosen one below it. Picking does nothing on its own; the footer button confirms.
 *
 * `sources` arrive already filtered and sorted (copySourcesFrom). `hasContent` says the prescription
 * being written already holds something the copy would replace, which changes the button's wording
 * and adds a warning next to it.
 */
const CopyPrescriptionModal = ({
  patientName,
  currentPrescriptionId,
  sources,
  hasContent = false,
  busy = false,
  onConfirm,
  onClose,
}) => {
  const [selectedId, setSelectedId] = useState(null);
  const previewRef = useRef(null);
  const list = Array.isArray(sources) ? sources : [];
  const selected = list.find((s) => s.prescriptionId === selectedId && s.prescriptionId !== currentPrescriptionId) || null;
  // A long-standing patient can have years of prescriptions, so the list pages.
  const paging = usePagedRows(list, { id: 'copyRx.sources', initialSize: 10 });

  // Nothing may close the dialog while the copy is running: its answer lands on this prescription.
  const close = () => {
    if (!busy) onClose();
  };
  // Pick-only: nothing typed here is worth a "discard changes?" question. Focus starts on the first
  // row so Enter picks it straight away.
  const dialogRef = useDialog(close, { confirmDiscard: false, initialFocus: 'tbody tr' });

  // The preview sits under the table; on a long list it would otherwise open out of sight.
  useEffect(() => {
    const el = previewRef.current;
    if (selectedId && el && typeof el.scrollIntoView === 'function') el.scrollIntoView({ block: 'nearest' });
  }, [selectedId]);

  const disabledReason = list.length === 0
    ? 'There is no earlier prescription to copy.'
    : !selected ? 'Select a prescription above to copy it.' : '';

  const confirmLabel = hasContent ? 'Replace with this prescription' : 'Copy into this prescription';

  return (
    // useDialog makes the outer element the dialog (role and aria-modal), so it carries the name too.
    <div
      ref={dialogRef}
      aria-labelledby="copy-rx-title"
      className="fixed inset-0 bg-black/60 flex items-center justify-center z-[60] p-4"
      onClick={(e) => { if (e.target === e.currentTarget) close(); }}
    >
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-4xl max-h-[90vh] flex flex-col overflow-hidden">
        <div className="bg-gradient-to-r from-navy-800 to-navy-700 px-6 py-4 flex items-center justify-between gap-3 flex-shrink-0">
          <div className="flex items-center gap-3 min-w-0">
            <div className="w-10 h-10 bg-white/20 rounded-xl flex items-center justify-center flex-shrink-0">
              <Copy className="w-5 h-5 text-white" aria-hidden="true" />
            </div>
            <div className="min-w-0">
              <h2 id="copy-rx-title" className="text-xl font-bold text-white leading-tight">Copy a previous prescription</h2>
              {patientName && <p className="text-emerald-100 text-sm truncate">{patientName}</p>}
            </div>
          </div>
          <button
            type="button"
            aria-label="Close"
            onClick={close}
            disabled={busy}
            className="p-1.5 hover:bg-white/20 rounded-lg text-white flex-shrink-0 disabled:opacity-50"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-6 space-y-4 min-h-0">
          {list.length === 0 ? (
            <p className="text-sm text-gray-600">This patient has no earlier completed prescription to copy.</p>
          ) : (
            <>
              <p className="text-sm text-gray-600">
                Pick the prescription to start from. Medicines are checked against today's stock and anything
                unavailable is left out and listed. Vitals, private notes and reports are never copied.
              </p>

              <div className="border border-gray-200 rounded-xl overflow-hidden">
                <TablePager paging={paging} noun="prescription" className="px-4" />
                <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-gray-50 border-b border-gray-200">
                    <tr className="text-xs font-semibold text-gray-500 uppercase tracking-wider text-left">
                      <th className="px-4 py-2.5">Date</th>
                      <th className="px-4 py-2.5">Rx no.</th>
                      <th className="px-4 py-2.5">Doctor</th>
                      <th className="px-4 py-2.5">Diagnosis</th>
                      <th className="px-4 py-2.5 text-center">Medicines</th>
                      <th className="px-4 py-2.5 text-center">Lab tests</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {paging.pageRows.map((s) => {
                      const isSelected = s.prescriptionId === selectedId;
                      const pick = () => { if (!busy) setSelectedId(s.prescriptionId); };
                      return (
                        <tr
                          key={s.prescriptionId}
                          tabIndex={0}
                          onClick={pick}
                          onKeyDown={onActivateKey(pick)}
                          data-selected={isSelected ? 'true' : undefined}
                          className={`cursor-pointer transition-colors ${
                            isSelected ? 'bg-emerald-50 shadow-[inset_3px_0_0_0_rgb(var(--c-emerald-600))]' : 'hover:bg-emerald-50/50'
                          } ${ROW_FOCUS_RING}`}
                        >
                          <td className="px-4 py-2.5 whitespace-nowrap text-gray-700">
                            {isSelected && <span className="sr-only">Selected: </span>}
                            {copySourceDate(s.prescriptionDate || s.createdAt)}
                          </td>
                          <td className="px-4 py-2.5 whitespace-nowrap font-medium text-gray-900">{s.prescriptionNumber}</td>
                          <td className="px-4 py-2.5 text-gray-700">
                            <div className="whitespace-nowrap">{s.doctorName}</div>
                            {s.doctorSpecialization && <div className="text-xs text-gray-500">{s.doctorSpecialization}</div>}
                          </td>
                          <td className="px-4 py-2.5 text-gray-700 max-w-[260px]">
                            <div className="truncate" title={s.diagnosis || ''}>{s.diagnosis}</div>
                          </td>
                          <td className="px-4 py-2.5 text-center text-gray-700">{s.medicineCount ?? 0}</td>
                          <td className="px-4 py-2.5 text-center text-gray-700">{s.labTestCount ?? 0}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
                </div>
                <TablePager paging={paging} noun="prescription" position="bottom" className="px-4" />
              </div>

              {selected && <CopyPreview ref={previewRef} summary={selected} />}
            </>
          )}
        </div>

        <div className="flex-shrink-0 px-6 py-4 border-t border-gray-200 bg-gray-50 space-y-3">
          {hasContent && list.length > 0 && (
            <div className="flex items-start gap-2 text-sm text-amber-800 bg-amber-50 border border-amber-200 rounded-lg p-3">
              <AlertTriangle className="w-4 h-4 mt-0.5 flex-shrink-0" aria-hidden="true" />
              <span>
                This replaces the complaints, notes, examination, diagnosis, medicines, lab tests, instructions and
                follow-up already entered.
              </span>
            </div>
          )}
          <div className="flex items-center justify-end gap-3 flex-wrap">
            {disabledReason && (
              <span id="copy-rx-disabled-reason" className="text-xs text-gray-500 mr-auto">{disabledReason}</span>
            )}
            <button
              type="button"
              onClick={close}
              disabled={busy}
              className="px-4 py-2 rounded-lg text-sm font-medium text-gray-700 bg-white border border-gray-300 hover:bg-gray-100 disabled:opacity-50"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={() => { if (selected && !busy) onConfirm(selected); }}
              disabled={!selected || busy}
              aria-describedby={disabledReason ? 'copy-rx-disabled-reason' : undefined}
              className="inline-flex items-center gap-2 px-5 py-2 rounded-lg text-sm font-semibold bg-emerald-600 text-white hover:bg-emerald-700 shadow-sm disabled:bg-gray-200 disabled:text-gray-400 disabled:shadow-none disabled:cursor-not-allowed"
            >
              {busy ? <RefreshCw className="w-4 h-4 animate-spin" aria-hidden="true" /> : <Copy className="w-4 h-4" aria-hidden="true" />}
              {busy ? 'Copying…' : confirmLabel}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

// One labelled line of the preview. At module scope: a component declared inside render is a new
// type on every render, so React would remount it each time.
const PreviewRow = ({ icon: Icon, label, children }) => (
  <div className="flex items-start gap-3">
    <div className="flex items-center gap-1.5 w-28 flex-shrink-0 text-xs font-semibold text-gray-500 uppercase tracking-wider pt-0.5">
      <Icon className="w-3.5 h-3.5" aria-hidden="true" />{label}
    </div>
    <div className="min-w-0 flex-1 text-sm text-gray-800">{children}</div>
  </div>
);

// What the chosen prescription holds, from the summary already loaded, so picking a row costs no
// request. The server copies from the full record; this is only for the doctor to recognise it.
const CopyPreview = React.forwardRef(({ summary }, ref) => {
  const meds = Array.isArray(summary.medicines) ? summary.medicines : [];
  const labs = (Array.isArray(summary.labTestNames) ? summary.labTestNames : []).filter(Boolean);
  return (
    <section ref={ref} aria-label="Preview" className="rounded-xl border border-emerald-200 bg-white p-4 space-y-3">
      <div className="text-sm font-semibold text-emerald-800">
        Preview of {summary.prescriptionNumber}
      </div>
      <PreviewRow icon={Stethoscope} label="Diagnosis">
        {summary.diagnosis ? <span className="whitespace-pre-wrap">{summary.diagnosis}</span> : <span className="text-gray-400">None recorded</span>}
      </PreviewRow>
      <PreviewRow icon={Pill} label="Medicines">
        {meds.length === 0 ? (
          <span className="text-gray-400">None</span>
        ) : (
          <ul className="space-y-1">
            {meds.map((m, i) => (
              <li key={i}>
                <span className="font-medium text-gray-900">{m.name}</span>
                {m.strength && <span className="text-gray-500 ml-1">{m.strength}</span>}
                {m.dosage && <span className="text-gray-500 ml-1">· {m.dosage}</span>}
              </li>
            ))}
          </ul>
        )}
      </PreviewRow>
      <PreviewRow icon={FlaskConical} label="Lab tests">
        {labs.length === 0 ? <span className="text-gray-400">None</span> : labs.join(', ')}
      </PreviewRow>
      <PreviewRow icon={Calendar} label="Follow-up">
        {summary.followupDays ? `After ${summary.followupDays} day${Number(summary.followupDays) === 1 ? '' : 's'}` : <span className="text-gray-400">None</span>}
      </PreviewRow>
      <PreviewRow icon={User} label="Doctor">
        {summary.doctorName || <span className="text-gray-400">Not recorded</span>}
        {summary.doctorSpecialization && <span className="text-gray-500"> ({summary.doctorSpecialization})</span>}
      </PreviewRow>
    </section>
  );
});
CopyPreview.displayName = 'CopyPreview';

export default CopyPrescriptionModal;
